import { ContentBlockShape, ContentBlockType, RoundShape } from "@/models";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { isEqual } from "lodash";

export const contentBlockAtom = atom<{ [index: string]: ContentBlockShape }>(
    {},
);
export const contentBlockPathAtom = atom<{ [index: string]: string }>({});
contentBlockAtom.debugLabel = "contentBlockAtom";
export const lessonOrRoundAtom = atom<
    App.Data.RoundOrLessonData | RoundShape | false
>(false);

function flattenContentBlocks(contentBlocks: ContentBlockShape[]) {
    const flattened = contentBlocks?.reduce((acc, block) => {
        acc.push(block);
        if (block.contentBlocks) {
            acc.push(...flattenContentBlocks(block.contentBlocks));
        }
        return acc;
    }, []);

    return flattened?.sort((a, b) => a.weight - b.weight) || [];
}

function getPath(
    block: ContentBlockShape,
    contentBlocks: { [index: string]: ContentBlockShape },
) {
    if (!block || !block.id) {
        throw new Error("Invalid content block passed to buildDotPathToRoot");
    }
    const path = [];

    function recurse(currentBlock: ContentBlockShape) {
        if (!currentBlock) {
            return
        }

        path.unshift(currentBlock.id);

        const parentId = currentBlock.parent_content_block_id;
        if (parentId) {
            const parentBlock = contentBlocks[parentId];
            recurse(parentBlock);
        }
    }

    recurse(block);
    return path.join(".");
}

export function useContentBlockPaths() {
    const [contentBlocks] = useAtom(contentBlockAtom);
    const [contentBlockPaths, setContentBlockPaths] =
        useAtom(contentBlockPathAtom);

    useEffect(() => {
        setContentBlockPaths(
            Object.fromEntries(
                Object.entries(contentBlocks).map(([id, block]) => [
                    id,
                    getPath(block, contentBlocks),
                ]),
            ),
        );
    }, [contentBlocks]);

    return { contentBlockPaths, setContentBlockPaths };
}

export function useSetupContentBlockState(
    initialLesson: App.Data.RoundOrLessonData | RoundShape,
    initialContentBlocks?: ContentBlockShape[],
) {
    const [contentBlocks, setContentBlocks] = useAtom(contentBlockAtom);
    const [_, setRoundOrLesson] = useAtom(lessonOrRoundAtom);
    setRoundOrLesson(initialLesson);

    const mappedContentBlocks: { [index: string]: ContentBlockShape } =
        useMemo(() => {
            const initialBlocks = initialContentBlocks?.length
                ? initialContentBlocks
                : initialLesson.contentBlocks;
            const flattened = flattenContentBlocks(initialBlocks as ContentBlockShape[]);
            return flattened.reduce((acc, block) => {
                acc[block.id] = block;
                return acc;
            }, {});
        }, [
            initialLesson,
            initialContentBlocks?.length,
            initialContentBlocks?.[0]?.id,
        ]);

    useEffect(() => {
        setContentBlocks(mappedContentBlocks);
    }, [mappedContentBlocks]);

    return contentBlocks;
}

export function useContentBlockState() {
    const [contentBlocks, setContentBlocks] = useAtom(contentBlockAtom);
    const [contentBlockPaths, setContentBlockPaths] =
        useAtom(contentBlockPathAtom);
    const [roundOrLesson] = useAtom(lessonOrRoundAtom);

    const editableParentTypes = [ContentBlockType.Page];

    const parentContentBlocks = useMemo(() => {
        if(!Object.values(contentBlocks).length) {
            return [];
        }
        const page = Object.values(contentBlocks).find((block) =>
            editableParentTypes.includes(block.content_block_type),
        );

        if (page) {
            return [
                ...Object.values(contentBlocks).filter(
                    (block) => !block.parent_content_block_id,
                ),
            ];
        }

        return Object.values(contentBlocks)
            .filter((block) => !block.parent_content_block_id)
            .sort((a, b) => a.weight - b.weight);
    }, [contentBlocks]);

    const editableContentBlocks = useMemo(() => {
        const page = Object.values(contentBlocks).find((block) =>
            editableParentTypes.includes(block.content_block_type),
        );

        const modalIds = Object.values(contentBlocks)
            .filter(
                (block) => block.content_block_type === ContentBlockType.Modal,
            )
            .map((block) => block.id);

        if (page) {
            return Object.values(contentBlocks)
                .filter(
                    (block) =>
                        block.parent_content_block_id === page?.id ||
                        modalIds.includes(block.parent_content_block_id),
                )
                .sort((a, b) => a.weight - b.weight);
        }

        return Object.values(contentBlocks)
            .filter((block) => !block.parent_content_block_id)
            .sort((a, b) => a.weight - b.weight);
    }, [contentBlocks]);

    const setContentBlock = useCallback(
        (block: ContentBlockShape) => {
            if (!block) return;
            if (!isEqual(contentBlocks[block.id], block)) {
                setContentBlocks((prev) => {
                    return {
                        ...prev,
                        [block.id]: block,
                    };
                });
            }
            block?.contentBlocks?.forEach((child) => {
                setContentBlock(child);
            });
        },
        [contentBlocks, parentContentBlocks],
    );

    const deleteContentBlock = useCallback(
        (blockId: string) => {
            setContentBlocks((prev) => {
                const newBlocks = { ...prev };
                delete newBlocks[blockId];
                return newBlocks;
            });
        },
        [contentBlocks],
    );

    const getParentByType = useCallback(
        (type: ContentBlockType, contentBlock: ContentBlockShape) => {
            if (!contentBlock) return null;
            if (contentBlock.content_block_type === type) return contentBlock;
            if (contentBlock.parent_content_block_id) {
                return getParentByType(
                    type,
                    contentBlocks[contentBlock.parent_content_block_id],
                );
            }
            return null;
        },
        [contentBlocks],
    );

    const removeContentBlockFromLocalState = useCallback(
        (blockId: string) => {
            setContentBlocks((prev) => {
                const newBlocks = { ...prev };
                delete newBlocks[blockId];
                return newBlocks;
            });
        },
        [contentBlocks],
    );

    return {
        contentBlocks,
        setContentBlock,
        setContentBlocks,
        deleteContentBlock,
        parentContentBlocks,
        getParentByType,
        roundOrLesson,
        lesson: roundOrLesson,
        editableContentBlocks,
        editableContentBlockIds: editableContentBlocks.map((block) => block.id),
        contentBlockPaths,
        setContentBlockPaths,
        removeContentBlockFromLocalState
    };
}

export function useGetContentBlockWithChildren(id: string) {
    const [contentBlocks] = useAtom(contentBlockAtom);

    const getContentBlockWithChildren = useCallback(
        (
            contentBlock: ContentBlockShape,
            contentBlocks: ContentBlockShape[],
        ): ContentBlockShape | null => {
            if (!contentBlock) return null;
            const childBlocks = contentBlocks
                .filter(
                    (block) =>
                        block.parent_content_block_id === contentBlock.id,
                )
                .sort((a, b) => a.weight - b.weight);

            if (!childBlocks?.length) {
                if (contentBlock?.contentBlocks?.length) {
                    return {
                        ...contentBlock,
                        contentBlocks: contentBlock.contentBlocks.sort(
                            (a, b) => a.weight - b.weight,
                        ),
                    };
                }

                return {
                    ...contentBlock,
                    contentBlocks: [],
                };
            }

            return {
                ...contentBlock,
                contentBlocks: childBlocks?.map((child) => {
                    return getContentBlockWithChildren(child, contentBlocks);
                }),
            };
        },
        [contentBlocks, id],
    );

    const contentBlockWithChildren = useMemo(() => {
        const contentBlock = contentBlocks[id];

        if (!contentBlock) {
            return null;
        }
        return getContentBlockWithChildren(
            contentBlock,
            Object.values(contentBlocks),
        );
    }, [contentBlocks, id]);

    return contentBlockWithChildren;
}

export function useGetContentBlockWithDirectChildren(id: string) {
    const [contentBlocks] = useAtom(contentBlockAtom);

    const getContentBlockWithChildren = useCallback(
        (
            contentBlock: ContentBlockShape,
            contentBlocks: ContentBlockShape[],
        ): ContentBlockShape | null => {
            if (!contentBlock) return null;
            const childBlocks = contentBlocks
                .filter(
                    (block) =>
                        block.parent_content_block_id === contentBlock.id,
                )
                .sort((a, b) => a.weight - b.weight);

            if (!childBlocks?.length) {
                if (contentBlock?.contentBlocks?.length) {
                    return {
                        ...contentBlock,
                        contentBlocks: contentBlock.contentBlocks.sort(
                            (a, b) => a.weight - b.weight,
                        ),
                    };
                }

                return {
                    ...contentBlock,
                    contentBlocks: [],
                };
            }

            return {
                ...contentBlock,
                contentBlocks: childBlocks,
            };
        },
        [contentBlocks],
    );

    const contentBlockWithChildren = useMemo(() => {
        const contentBlock = contentBlocks[id];

        if (!contentBlock) {
            return null;
        }
        return getContentBlockWithChildren(
            contentBlock,
            Object.values(contentBlocks),
        );
    }, [contentBlocks]);

    return contentBlockWithChildren;
}

export function useChildContentBlocksOfType(
    contentBlock: ContentBlockShape,
    type: ContentBlockType,
) {
    const block = useGetContentBlockWithChildren(contentBlock?.id);

    function getChildContentBlocksOfType(
        contentBlock: ContentBlockShape,
        type: ContentBlockType,
    ) {
        const childrenOfType = contentBlock.contentBlocks.filter(
            (child) => child.content_block_type === type,
        );

        for (const child of contentBlock.contentBlocks) {
            childrenOfType.push(...getChildContentBlocksOfType(child, type));
        }

        return childrenOfType;
    }

    return useMemo(() => {
        if (!block) return [];
        return getChildContentBlocksOfType(block, type);
    }, [block, type]);
}
